import type { ParseableError } from '@nx/errors';
import { isParseableError } from '@nx/errors';
import { type PayloadAction, type SerializedError, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { isEmpty } from 'lodash-es';

import {
  INITIAL_FILTER,
  INITIAL_INPUT_TEXT,
  INITIAL_LOCKED_SUGGESTIONS,
  INITIAL_SELECTED_SUGGESTION_PATH_IDX,
} from '../../../modules/SearchBar/SearchBar.const';
import {
  isActionSuggestion,
  isNodeSuggestion,
  isRelationshipSuggestion,
  isSearchPhraseSuggestion,
  lockNodeSuggestion,
  lockRelationshipSuggestion,
} from '../../../modules/SearchBar/SearchBar.utils';
import type { ExploreError } from '../../../services/errors/errors';
import { runExpandThunk } from '../../contextmenu/contextmenu.thunks';
import { showNodesAndRelationships } from '../../graph/graph.thunks';
import { updateSearchTemplate } from '../../perspectives/perspectives';
import { fetchPropertyValueSuggestionsThunk } from '../suggestions/property-value-suggestions/property-value-suggestions.thunk';
import { fetchSearchPhraseSuggestionsThunk } from '../suggestions/search-phrase-suggestions/search-phrase-suggestions.thunk';
import { fetchSuggestionsThunk } from '../suggestions/search-suggestions.thunk';
import { fetchStructuredSuggestionsThunk } from '../suggestions/structured-suggestions/structured-suggestions.thunk';
import { NAME } from './search-core.const';
import { runSceneActionThunk, runSearchThunk } from './search-core.thunks';
import type { SearchCoreState } from './search-core.types';
import { STATUS } from './search-core.types';

export const initialState: SearchCoreState = {
  filter: INITIAL_FILTER,
  inputText: INITIAL_INPUT_TEXT,
  selectedSuggestionPathIdx: INITIAL_SELECTED_SUGGESTION_PATH_IDX,
  lockedSuggestions: INITIAL_LOCKED_SUGGESTIONS,
  labelsWithNodesUnderLimit: [],
  initialFocus: false,
  isGenAIAssistantEnabled: false,
  searchStatus: {
    status: STATUS.IDLE,
  },
};

const runThunks = [runSearchThunk, runSceneActionThunk, runExpandThunk] as const;

const suggestionsThunks = [
  fetchPropertyValueSuggestionsThunk,
  fetchSearchPhraseSuggestionsThunk,
  fetchStructuredSuggestionsThunk,
] as const;

const slice = createSlice({
  name: NAME,
  initialState,
  reducers: {
    updateFilter: (state, action: PayloadAction<SearchCoreState['filter']>) => {
      const filter = action.payload;
      state.filter = state.filter === filter ? INITIAL_FILTER : filter;
    },
    updateInputText: (state, action: PayloadAction<SearchCoreState['inputText']>) => {
      state.inputText = action.payload;
    },
    updateSelectedSuggestionPathIdx: (state, action: PayloadAction<SearchCoreState['selectedSuggestionPathIdx']>) => {
      state.selectedSuggestionPathIdx = action.payload;
    },
    updateLockedSuggestions: (state, action: PayloadAction<SearchCoreState['lockedSuggestions']>) => {
      state.lockedSuggestions = action.payload;
    },
    lockSuggestion: (state, action: PayloadAction<SearchCoreState['lockedSuggestions']>) => {
      let newLockedSuggestions = [...state.lockedSuggestions];
      const suggestion = action.payload;
      const head = suggestion?.at(0);
      let inputText = INITIAL_INPUT_TEXT;

      if (isRelationshipSuggestion(head)) {
        newLockedSuggestions = lockRelationshipSuggestion(suggestion, newLockedSuggestions);
      } else if (isNodeSuggestion(head)) {
        newLockedSuggestions = lockNodeSuggestion(suggestion, newLockedSuggestions);
      } else if (isSearchPhraseSuggestion(head)) {
        newLockedSuggestions = suggestion;
        inputText = head.text;
      } else if (isActionSuggestion(head)) {
        newLockedSuggestions = suggestion;
        inputText = head.title;
      }

      state.selectedSuggestionPathIdx = INITIAL_SELECTED_SUGGESTION_PATH_IDX;
      state.inputText = inputText;
      state.filter = INITIAL_FILTER;
      state.lockedSuggestions = newLockedSuggestions;
    },
    updateLabelsWithNodesUnderLimit: (state, action: PayloadAction<SearchCoreState['labelsWithNodesUnderLimit']>) => {
      state.labelsWithNodesUnderLimit = action.payload;
    },
    updateInitialFocus: (state, action: PayloadAction<SearchCoreState['initialFocus']>) => {
      state.initialFocus = action.payload;
    },
    updateIsGenAIAssistantEnabled: (state, action: PayloadAction<SearchCoreState['isGenAIAssistantEnabled']>) => {
      state.isGenAIAssistantEnabled = action.payload;
    },
    resetSearchState: (state) => {
      state.lockedSuggestions = INITIAL_LOCKED_SUGGESTIONS;
      state.selectedSuggestionPathIdx = INITIAL_SELECTED_SUGGESTION_PATH_IDX;
      state.inputText = INITIAL_INPUT_TEXT;
      state.filter = INITIAL_FILTER;
    },
    updateGenAiLastAskedQuestion: (
      state,
      action: PayloadAction<SearchCoreState['genAiLastAskedQuestion'] | undefined>,
    ) => {
      state.genAiLastAskedQuestion = action.payload;
    },
    updateStatus: (state, action: PayloadAction<SearchCoreState['searchStatus']>) => {
      state.searchStatus = action.payload;
    },
    removeLast: (state) => {
      if (isEmpty(state.lockedSuggestions)) {
        return;
      }

      state.selectedSuggestionPathIdx = INITIAL_SELECTED_SUGGESTION_PATH_IDX;

      const newLockedSuggestions = [...state.lockedSuggestions];
      let last = newLockedSuggestions.pop();

      if (isNodeSuggestion(last) || isRelationshipSuggestion(last)) {
        if (last.propertyConditionValue) {
          last.propertyConditionValue = undefined;
        } else if (last.propertyCondition) {
          last.propertyCondition = undefined;
        } else if (last.propertyName) {
          last.propertyName = undefined;
          last.propertyType = undefined;
        } else {
          last = undefined;
        }
      }

      state.lockedSuggestions = last ? [...newLockedSuggestions, last] : newLockedSuggestions;
    },
    removeFacetedResults: (state) => {
      state.facetedResults = { ...initialState.facetedResults };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(runSearchThunk.pending, (state) => {
        state.searchStatus = { status: STATUS.FETCHING_DATA };
      })
      .addCase(fetchSuggestionsThunk.pending, (state) => {
        state.searchStatus = { status: STATUS.FETCHING_SUGGESTIONS };
      })
      .addCase(updateSearchTemplate, (state, action) => {
        const shouldResetState = state.lockedSuggestions.some(
          (suggestion) => isSearchPhraseSuggestion(suggestion) && suggestion.id === action.payload.templateId,
        );
        if (shouldResetState) {
          state.lockedSuggestions = INITIAL_LOCKED_SUGGESTIONS;
          state.selectedSuggestionPathIdx = INITIAL_SELECTED_SUGGESTION_PATH_IDX;
          state.inputText = INITIAL_INPUT_TEXT;
          state.filter = INITIAL_FILTER;
        }
      })
      .addCase(runSearchThunk.fulfilled, (state, action) => {
        const { nodes, relationships, hasFacetedResults, sanitizedInputText } = action.payload;
        if (hasFacetedResults) {
          state.facetedResults = { nodes, relationships, sanitizedInputText };
          state.searchStatus = { status: STATUS.IDLE };
        }
      })
      .addCase(showNodesAndRelationships.fulfilled, (state, action) => {
        const { recordLimitHit, isUpdateQuery, error } = action.meta.arg;
        const { nodes, relationships } = action.payload;
        if (error) {
          state.searchStatus = {
            status: STATUS.FAILED,
            nodesCount: nodes.length,
            errors: [error],
          };
        } else {
          state.searchStatus = {
            status: STATUS.SUCCEEDED,
            nodesCount: nodes.length,
            relationshipsCount: relationships.length,
            isUpdateQuery,
            recordLimitHit,
          };
        }
      })
      .addMatcher(isAnyOf(...runThunks.map((thunk) => thunk.rejected)), (state, action) => {
        const error = parseError(action.payload?.error, action.error);

        state.searchStatus = {
          status: STATUS.FAILED,
          errors: [error],
          nodesCount: action.payload?.nodesCount,
        };
      })
      .addMatcher(isAnyOf(...suggestionsThunks.map((thunk) => thunk.rejected)), (state, action) => {
        const error = parseError(action.payload?.error, action.error);

        if (state.searchStatus.status === STATUS.FAILED) {
          state.searchStatus = {
            ...state.searchStatus,
            errors: [...state.searchStatus.errors, error],
          };
        } else {
          state.searchStatus = {
            status: STATUS.FAILED,
            errors: [error],
          };
        }
      });
  },
});

const parseError = (payloadError: ParseableError | ExploreError | undefined, actionError: SerializedError) =>
  isParseableError(payloadError)
    ? payloadError
    : { name: actionError.name ?? '', message: actionError.message ?? '', code: actionError.code ?? '' };

export const {
  updateFilter,
  updateInputText,
  updateSelectedSuggestionPathIdx,
  updateLockedSuggestions,
  lockSuggestion,
  updateLabelsWithNodesUnderLimit,
  updateInitialFocus,
  updateIsGenAIAssistantEnabled,
  resetSearchState,
  updateGenAiLastAskedQuestion,
  updateStatus,
  removeLast,
  removeFacetedResults,
} = slice.actions;
export default slice.reducer;
